import React, { useState, useEffect } from 'react';
import './addContainerFee.css';

const AddContainerFee = () => {
  const [chargeName, setChargeName] = useState('');
  const [shippingOption, setShippingOption] = useState('CIF');
  const [chargeValue, setChargeValue] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [containerFees, setContainerFees] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Global Settings
  const [yenPerDollar, setYenPerDollar] = useState(156);
  const [marginPercent, setMarginPercent] = useState(10);
  const [showSettings, setShowSettings] = useState(false);

  // Dialog state
  const [dialog, setDialog] = useState({
    show: false,
    type: 'success', // 'success' or 'error'
    title: '',
    message: ''
  });

  const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/container-fees';

  // Shipping option mapping
  const shippingOptionMap = {
    'CIF': 1,
    'C&F': 2,
    'FOB': 3
  };

  const reverseShippingOptionMap = {
    'CIF': 'CIF',
    'CNF': 'C&F',
    'FOB': 'FOB'
  };

  // Show dialog helper
  const showDialog = (type, title, message) => {
    setDialog({
      show: true,
      type,
      title,
      message
    });
  };

  // Close dialog helper
  const closeDialog = () => {
    setDialog({
      show: false,
      type: 'success',
      title: '',
      message: ''
    });
  };

  // Fetch all container fees on component mount
  useEffect(() => {
    fetchContainerFees();
  }, []);

  const fetchContainerFees = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/all/with-shipping-type`, {
        headers: {
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch container fees');
      }

      const data = await response.json();
      
      // Transform API data to match component state structure
      const transformedFees = data.map(fee => ({
        id: fee.containerFeesId,
        chargeName: fee.chargeName,
        shippingOption: reverseShippingOptionMap[fee.shippingOptionType] || fee.shippingOptionType,
        baseAmount: fee.dollarAmount / (1 + fee.margin / 100), // Calculate base from final amount
        amountWithMargin: fee.dollarAmount,
        currency: 'USD', // Default to USD as API stores both currencies
        dollarAmount: fee.dollarAmount,
        yenAmount: fee.yenAmount,
        perDollar: fee.perDollar,
        margin: fee.margin,
        shippingOptionId: fee.shippingOptionId,
        // Store original values for editing
        originalDollarAmount: fee.dollarAmount,
        originalYenAmount: fee.yenAmount
      }));

      setContainerFees(transformedFees);
      
      // Update global settings from first fee if available
      if (data.length > 0) {
        setYenPerDollar(data[0].perDollar);
        setMarginPercent(data[0].margin);
      }
    } catch (err) {
      setError(err.message);
      showDialog('error', 'Fetch Failed', err.message);
      console.error('Error fetching container fees:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateWithMargin = (amount) => {
    return amount * (1 + marginPercent / 100);
  };

  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    if (fromCurrency === 'USD' && toCurrency === 'YEN') {
      return amount * yenPerDollar;
    }
    if (fromCurrency === 'YEN' && toCurrency === 'USD') {
      return amount / yenPerDollar;
    }
    return amount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!chargeName || !chargeValue) {
      showDialog('error', 'Validation Error', 'Please fill in all required fields');
      return;
    }

    const baseAmount = parseFloat(chargeValue);
    const amountWithMargin = calculateWithMargin(baseAmount);
    
    // Convert to USD and YEN for API
    const dollarAmount = currency === 'USD' ? amountWithMargin : amountWithMargin / yenPerDollar;
    const yenAmount = currency === 'YEN' ? amountWithMargin : amountWithMargin * yenPerDollar;

    setLoading(true);
    setError('');

    try {
      if (editingId !== null) {
        // UPDATE API call
        const response = await fetch(`${API_BASE_URL}/update/${editingId}`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            chargeName,
            dollarAmount: dollarAmount,
            yenAmount: yenAmount,
            perDollar: yenPerDollar,
            margin: marginPercent
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to update container fee');
        }

        showDialog('success', 'Update Successful', result.message || 'Container fee updated successfully');
        setEditingId(null);
      } else {
        // SAVE API call
        const shippingOptionId = shippingOptionMap[shippingOption];
        
        const response = await fetch(`${API_BASE_URL}/save/container/fees`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            shippingOptionId: shippingOptionId,
            fees: [
              {
                chargeName,
                dollarAmount: dollarAmount,
                yenAmount: yenAmount,
                perDollar: yenPerDollar,
                margin: marginPercent
              }
            ]
          })
        });

        const result = await response.json();

        if (!response.ok || result.savedCount === 0) {
          throw new Error(result.message || 'Failed to save container fee');
        }

        showDialog('success', 'Save Successful', result.message || 'Container fee saved successfully');
      }

      // Refresh the list after save/update
      await fetchContainerFees();

      // Reset form
      setChargeName('');
      setShippingOption('CIF');
      setChargeValue('');
      setCurrency('USD');
    } catch (err) {
      setError(err.message);
      showDialog('error', editingId ? 'Update Failed' : 'Save Failed', err.message);
      console.error('Error saving/updating container fee:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (fee) => {
    setChargeName(fee.chargeName);
    setShippingOption(fee.shippingOption);
    
    // Set the original value from the API (without decimal formatting)
    // Determine which currency was used and set the original amount
    if (fee.currency === 'USD') {
      setChargeValue(fee.originalDollarAmount);
      setCurrency('USD');
    } else {
      setChargeValue(fee.originalYenAmount);
      setCurrency('YEN');
    }
    
    setEditingId(fee.id);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/delete/${id}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete container fee');
      }

      showDialog('success', 'Delete Successful', result.message || 'Container fee deleted successfully');

      // Refresh the list after delete
      await fetchContainerFees();

      // Clear form if editing the deleted item
      if (editingId === id) {
        setEditingId(null);
        setChargeName('');
        setShippingOption('CIF');
        setChargeValue('');
        setCurrency('USD');
      }
    } catch (err) {
      setError(err.message);
      showDialog('error', 'Delete Failed', err.message);
      console.error('Error deleting container fee:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (id) => {
    setDialog({
      show: true,
      type: 'confirm',
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this charge? This action cannot be undone.',
      onConfirm: () => {
        closeDialog();
        handleDelete(id);
      }
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setChargeName('');
    setShippingOption('CIF');
    setChargeValue('');
    setCurrency('USD');
  };

  const getTotalByOption = (option, curr) => {
    return containerFees
      .filter(fee => fee.shippingOption === option)
      .reduce((sum, fee) => {
        const amount = convertCurrency(fee.amountWithMargin, fee.currency, curr);
        return sum + amount;
      }, 0);
  };

  const getGrandTotal = (curr) => {
    return containerFees.reduce((sum, fee) => {
      const amount = convertCurrency(fee.amountWithMargin, fee.currency, curr);
      return sum + amount;
    }, 0);
  };

  return (
    <div className="container-fee-page">
      <div className="page-header">
        <h1>Container Fee Configuration</h1>
        <button 
          className="settings-toggle"
          onClick={() => setShowSettings(!showSettings)}
        >
          ⚙️ {showSettings ? 'Hide' : 'Settings'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="loading-message">
          ⏳ Loading...
        </div>
      )}

      {/* Dialog Modal */}
      {dialog.show && (
        <div className="dialog-overlay" onClick={dialog.type !== 'confirm' ? closeDialog : undefined}>
          <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
            <div className={`dialog-header ${dialog.type}`}>
              <span className="dialog-icon">
                {dialog.type === 'success' && '✅'}
                {dialog.type === 'error' && '❌'}
                {dialog.type === 'confirm' && '⚠️'}
              </span>
              <h3 className="dialog-title">{dialog.title}</h3>
            </div>
            <div className="dialog-body">
              <p className="dialog-message">{dialog.message}</p>
            </div>
            <div className="dialog-footer">
              {dialog.type === 'confirm' ? (
                <>
                  <button className="dialog-btn dialog-btn-cancel" onClick={closeDialog}>
                    Cancel
                  </button>
                  <button className="dialog-btn dialog-btn-confirm" onClick={dialog.onConfirm}>
                    Delete
                  </button>
                </>
              ) : (
                <button className="dialog-btn dialog-btn-primary" onClick={closeDialog}>
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Global Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-grid">
            <div className="setting-item">
              <label>💱 Exchange Rate</label>
              <div className="exchange-input">
                <span className="currency-label">$1 =</span>
                <input
                  type="number"
                  value={yenPerDollar}
                  onChange={(e) => setYenPerDollar(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="mini-input"
                />
                <span className="currency-label">¥</span>
              </div>
            </div>
            <div className="setting-item">
              <label>📊 Profit Margin</label>
              <div className="margin-input">
                <input
                  type="number"
                  value={marginPercent}
                  onChange={(e) => setMarginPercent(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="mini-input"
                />
                <span className="currency-label">%</span>
              </div>
            </div>
          </div>
          <div className="settings-info">
            <span>All charges will automatically include {marginPercent}% margin</span>
          </div>
        </div>
      )}

      <div className="content-wrapper">
        {/* Compact Form */}
        <div className="fee-card">
          <h3>{editingId ? '✏️ Edit' : '➕ Add'} Charge</h3>
          <form onSubmit={handleSubmit} className="compact-form">
            <div className="form-row">
              <input
                type="text"
                value={chargeName}
                onChange={(e) => setChargeName(e.target.value)}
                placeholder="Charge Name"
                className="input-field"
                disabled={loading}
              />
              <select
                value={shippingOption}
                onChange={(e) => setShippingOption(e.target.value)}
                className="input-field select-field"
                disabled={loading || editingId !== null}
              >
                <option value="CIF">CIF</option>
                <option value="C&F">C&F</option>
                <option value="FOB">FOB</option>
              </select>
              
              <div className="amount-group">
                <input
                  type="number"
                  value={chargeValue}
                  onChange={(e) => setChargeValue(e.target.value)}
                  placeholder="Amount"
                  min="0"
                  step="0.01"
                  className="input-field amount-input"
                  disabled={loading}
                />
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="currency-select"
                  disabled={loading}
                >
                  <option value="USD">USD</option>
                  <option value="YEN">YEN</option>
                </select>
              </div>

              {chargeValue && (
                <div className="margin-preview">
                  +{marginPercent}% = {calculateWithMargin(parseFloat(chargeValue)).toFixed(2)}
                </div>
              )}
              
              <div className="btn-group">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? '⏳' : (editingId ? '💾' : '➕')}
                </button>
                {editingId && (
                  <button type="button" onClick={handleCancel} className="btn-secondary" disabled={loading}>
                    ✕
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Three Column Grid */}
        <div className="fees-grid">
          {['CIF', 'C&F', 'FOB'].map((option) => {
            const optionFees = containerFees.filter(fee => fee.shippingOption === option);
            
            return (
              <div key={option} className="option-card">
                <div className="card-header">
                  <span className={`badge badge-${option.toLowerCase().replace('&', '')}`}>
                    {option}
                  </span>
                  <div className="dual-currency">
                    <span className="mini-total">${getTotalByOption(option, 'USD').toFixed(2)}</span>
                    <span className="mini-total">¥{getTotalByOption(option, 'YEN').toFixed(0)}</span>
                  </div>
                </div>
                
                <div className="charges-list">
                  {optionFees.length === 0 ? (
                    <div className="empty">No charges</div>
                  ) : (
                    optionFees.map((fee) => (
                      <div key={fee.id} className={`charge-item ${editingId === fee.id ? 'editing' : ''}`}>
                        <div className="charge-info">
                          <div className="charge-main">
                            <span className="charge-name">{fee.chargeName}</span>
                            <div className="charge-amounts">
                              <span className="base-amount">
                                {fee.currency === 'USD' ? '$' : '¥'}{fee.baseAmount.toFixed(2)}
                              </span>
                              <span className="final-amount">
                                {fee.currency === 'USD' ? '$' : '¥'}{fee.amountWithMargin.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="dual-display">
                            <span className="converted-usd">
                              ${convertCurrency(fee.amountWithMargin, fee.currency, 'USD').toFixed(2)}
                            </span>
                            <span className="converted-yen">
                              ¥{convertCurrency(fee.amountWithMargin, fee.currency, 'YEN').toFixed(0)}
                            </span>
                          </div>
                        </div>
                        <div className="charge-actions">
                          <button 
                            onClick={() => handleEdit(fee)} 
                            className="icon-btn edit"
                            disabled={loading}
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDeleteConfirm(fee.id)} 
                            className="icon-btn delete"
                            disabled={loading}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Bar */}
        {containerFees.length > 0 && (
          <div className="summary-bar">
            <span>Total: {containerFees.length} charges</span>
            <div className="grand-totals">
              <span className="grand-total usd">
                💵 ${getGrandTotal('USD').toFixed(2)}
              </span>
              <span className="grand-total yen">
                💴 ¥{getGrandTotal('YEN').toFixed(0)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddContainerFee;